import React from 'react';
import { useThemeColors } from '../../hooks/useThemeColors';
import './RiskTrendPill.css';

/**
 * RiskTrendPill — small colored pill conveying a worsening / improving / stable
 * risk trend for an asset row.
 *
 * Polarity (PRD-51): higher risk score = riskier, so:
 *   worsening → ↑ Increasing   (critical color)
 *   improving → ↓ Decreasing   (healthy color)
 *   stable    → = Stable       (muted text color)
 *
 * Accessibility: the arrow glyph + word both carry the signal so color is
 * never the only direction cue (PRD-68 §3.1 a11y requirement).
 */
function RiskTrendPill({ trend = 'stable', className = '' }) {
    const colors = useThemeColors();

    const config = {
        worsening: {
            arrow: '↑', // ↑
            word: 'Increasing',
            color: colors.severity.critical,
            ariaLabel: 'Risk increasing',
        },
        improving: {
            arrow: '↓', // ↓
            word: 'Decreasing',
            color: colors.risk.healthy,
            ariaLabel: 'Risk decreasing',
        },
        stable: {
            arrow: '=', // =
            word: 'Stable',
            color: colors.text.muted,
            ariaLabel: 'Risk stable',
        },
    };

    const c = config[trend] || config.stable;

    return (
        <span
            className={`risk-trend-pill risk-trend-pill--${trend} ${className}`.trim()}
            style={{
                color: c.color,
                borderColor: c.color,
            }}
            aria-label={c.ariaLabel}
            role="status"
        >
            <span className="risk-trend-pill__arrow" aria-hidden="true">{c.arrow}</span>
            <span className="risk-trend-pill__word">{c.word}</span>
        </span>
    );
}

export default RiskTrendPill;
